import { ChangeEvent, useState } from "react";
import { Button, Input } from "reactstrap";
import useQuestionGenerator from "../../../../api/api-hooks/brie/useQuestionGenerator";
import AppSpinner from "../../../../elements/AppSpinner";
import {
  ActionIcon,
  CopyButton,
  Select,
  Textarea,
  Tooltip,
} from "@mantine/core";

const QuestionGenerator = () => {
  const [text, setText] = useState<string>("");
  const [questionCount, setQuestionsCount] = useState<number>(10);
  const [questionType, setQuestionsType] = useState<string>("open-ended");
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const {
    questionGenerator,
    questionGeneratorLoading,
    questionGeneratorValue,
  } = useQuestionGenerator();

  const handleClick = () => {
    if (!text || !questionCount || !questionType) return;
    questionGenerator({
      text,
      questionCount,
      questionType,
      powered: "open-ai",
    });
    setShowQuestionnaire(true);
  };

  const handleRestart = () => {
    setText("");
    setQuestionsCount(10);
    setQuestionsType("open-ended");
    setShowQuestionnaire(false);
  };

  return (
    <>
      <div
        className="d-flex flex-column"
        style={{
          boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
          padding: "24px",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        <div className="d-flex w-100 align-items-center mb-3">
          <i
            className="mdi mdi-chat-question-outline mr-3"
            style={{ fontSize: "32px" }}
          ></i>
          <h2 className="m-0">Question Generator</h2>
        </div>
        <p
          className="m-0 mb-1"
          style={{
            fontSize: "13px",
            fontWeight: 400,
            letterSpacing: "0.0025em",
          }}
        >
          Enter the text from which you want to generate questions:
        </p>
        <Input
          placeholder="Enter your text here"
          rows={10}
          type="textarea"
          maxLength={4096}
          disabled={questionGeneratorLoading}
          onChange={(e: any) => setText(e.target.value)}
          value={text}
        />
        <div className="mt-2 mb-4 d-flex justify-content-end text-sm text-medium">
          {text?.length ?? 0} / 4096
        </div>
        <p className="text-sm mt-2 mb-2">Setup number of questions</p>
        <Input
          type="number"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const newCount = e.target.value;

            if (Number(newCount) > 10) return;

            setQuestionsCount(parseInt(newCount));
          }}
          className="form-control"
          value={questionCount}
          placeholder="Number of questions generated. Limit 10"
          bsSize="sm"
          disabled={questionGeneratorLoading}
        />
        <p className="text-sm mt-4 mb-2">Questions type</p>
        <Select
          placeholder="Pick one"
          data={[
            { value: "open-ended", label: "Open-ended" },
            { value: "multiple-choice", label: "Multiple-choice" },
          ]}
          onChange={(type: string | null) => setQuestionsType(type!)}
          className="mb-4"
          value={questionType}
          disabled={questionGeneratorLoading}
        />
        {questionGeneratorLoading ? (
          <AppSpinner className="ml-auto" size="md" />
        ) : (
          <div className="mb-3 d-flex justify-content-end">
            {questionGeneratorValue && (
              <Button
                onClick={() => handleRestart()}
                size="sm"
                className="m-0 p-0 mr-3"
                outline
                style={{ width: "65px", height: "32px", marginLeft: "10px" }}
              >
                Restart
              </Button>
            )}
            <Button
              onClick={() => handleClick()}
              color="primary"
              size="sm"
              className="m-0 p-0"
              style={{ width: "90px", height: "32px", marginLeft: "10px" }}
              disabled={!text || !questionCount || !questionType}
            >
              Generate
            </Button>
          </div>
        )}
        {questionGeneratorValue && showQuestionnaire && (
          <div className="d-flex flex-column justify-content-start">
            <span
              style={{
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: "22px",
                color: "#26B058",
              }}
              className="mb-2"
            >
              Questionnaire:
            </span>

            <Textarea
              autosize
              disabled={questionGeneratorLoading}
              value={questionGeneratorValue}
              rightSection={
                <CopyButton value={questionGeneratorValue} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied" : "Copy"}
                      withArrow
                      position="right"
                    >
                      <ActionIcon
                        variant="transparent"
                        color={copied ? "teal" : "gray"}
                        onClick={copy}
                        className="mr-3 position-absolute"
                        style={{ top: 10 }}
                      >
                        {copied ? (
                          <i
                            className="mdi mdi-check"
                            style={{ fontSize: "24px", top: "30px" }}
                          ></i>
                        ) : (
                          <i
                            className="mdi mdi-content-copy"
                            style={{ fontSize: "24px" }}
                          ></i>
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

export default QuestionGenerator;
